import React, { useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import {
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Card,
  OverlayTrigger,
  Tooltip as BsTooltip,
} from 'react-bootstrap';
import {
  FaSearch,
  FaCalendarAlt,
  FaExclamationCircle,
  FaChartPie,
  FaEraser,
} from 'react-icons/fa';
import { Formik } from 'formik';
import Select from 'react-select';
import MaskedInput from 'react-text-mask';
import moment from 'moment';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Chart, Pie } from 'react-chartjs-2';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import { toast } from 'react-toastify';
import HeaderAzul from '~/Components/HeaderAzul/HeaderAzul';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import * as constants from '~/constant';
import { consultarDashboardVendasImobiliaria } from '~/Services/Service';
import { formatarMonetario } from '~/Utils/Helpers';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend
);

const CORES_GRAFICO = [
  'rgba(54, 162, 235, 0.8)',
  'rgba(255, 99, 132, 0.8)',
  'rgba(75, 192, 192, 0.8)',
  'rgba(255, 206, 86, 0.8)',
  'rgba(153, 102, 255, 0.8)',
  'rgba(255, 159, 64, 0.8)',
  'rgba(199, 199, 199, 0.8)',
  'rgba(83, 102, 255, 0.8)',
  'rgba(255, 99, 255, 0.8)',
  'rgba(99, 255, 132, 0.8)',
  'rgba(255, 180, 99, 0.8)',
  'rgba(102, 255, 255, 0.8)',
  'rgba(180, 99, 255, 0.8)',
  'rgba(255, 255, 99, 0.8)',
  'rgba(99, 132, 255, 0.8)',
];

const validationSchema = Yup.object().shape({
  data_inicial: Yup.string()
    .matches(/^\d{2}\/\d{2}\/\d{4}$/, 'Formato de data inválido')
    .test('valid-date', 'Data inválida', value => {
      if (!value) return true;
      return moment(value, 'DD/MM/YYYY', true).isValid();
    }),
  data_final: Yup.string()
    .matches(/^\d{2}\/\d{2}\/\d{4}$/, 'Formato de data inválido')
    .test('valid-date', 'Data inválida', value => {
      if (!value) return true;
      return moment(value, 'DD/MM/YYYY', true).isValid();
    }),
});

function montarDadosPizza(dados, label) {
  if (!dados || !dados.length) return null;

  const total = dados.reduce((acc, item) => acc + (item.valor || 0), 0);

  return {
    labels: dados.map(item => item.categoria || 'N/I'),
    datasets: [
      {
        label,
        data: dados.map(item => item.valor || 0),
        backgroundColor: dados.map((_, i) => CORES_GRAFICO[i % CORES_GRAFICO.length]),
        borderWidth: 1,
      },
    ],
    _total: total,
    _itens: dados,
  };
}

function montarDadosPizzaQuantidade(dados, label) {
  if (!dados || !dados.length) return null;

  const total = dados.reduce((acc, item) => acc + (item.quantidade || 0), 0);

  return {
    labels: dados.map(item => item.categoria || 'N/I'),
    datasets: [
      {
        label,
        data: dados.map(item => item.quantidade || 0),
        backgroundColor: dados.map((_, i) => CORES_GRAFICO[i % CORES_GRAFICO.length]),
        borderWidth: 1,
      },
    ],
    _total: total,
    _itens: dados,
  };
}

function criarOptionsPizza(dadosPizza, formatarValor) {
  const total = (dadosPizza && dadosPizza._total) || 0;
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { boxWidth: 12, font: { size: 11 } },
      },
      tooltip: {
        callbacks: {
          label: context => {
            const value = context.parsed || 0;
            const perc =
              total > 0 ? ((value / total) * 100).toFixed(1) + '%' : '0%';
            const valorFormatado = formatarValor
              ? formatarMonetario(value, false)
              : value.toLocaleString('pt-BR');
            return `${context.label}: ${valorFormatado} (${perc})`;
          },
        },
      },
    },
  };
}

export default function RelatorioVendasImobiliaria() {
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState(null);
  const [openDatePickerInicial, setOpenDatePickerInicial] = useState(false);
  const [openDatePickerFinal, setOpenDatePickerFinal] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const empreendimentos = useSelector(state => state.empreendimentos);
  const gerentesCorretores = useSelector(state => state.corretor);

  const optionsEmpreendimentos = useMemo(
    () =>
      empreendimentos.sort.map(item => ({
        value: item.codigo,
        label: item.codigo + ' - ' + item.descricao,
      })),
    [empreendimentos]
  );

  const optionsImobiliarias = useMemo(
    () =>
      gerentesCorretores.listaGerentes.map(ger => ({
        value: ger.codigo,
        label: ger.nome_imobiliaria,
      })),
    [gerentesCorretores]
  );

  const dadosGraficoVgvCorretores = useMemo(() => {
    if (!dados || !dados.vgv_por_corretores || !dados.vgv_por_corretores.length) return null;
    return {
      labels: dados.vgv_por_corretores.map(item => item.categoria || 'N/I'),
      datasets: [
        {
          type: 'bar',
          label: 'VGV (R$)',
          data: dados.vgv_por_corretores.map(item => item.valor || 0),
          backgroundColor: 'rgba(54, 162, 235, 0.7)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
          yAxisID: 'y',
          order: 2,
        },
        {
          type: 'line',
          label: 'Quantidade',
          data: dados.vgv_por_corretores.map(item => item.quantidade || 0),
          borderColor: 'rgba(255, 99, 132, 1)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          borderWidth: 2,
          pointRadius: 4,
          yAxisID: 'y1',
          order: 1,
        },
      ],
    };
  }, [dados]);

  const optionsGraficoVgvCorretores = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: function(context) {
            if (context.datasetIndex === 1) {
              return context.dataset.label + ': ' + context.parsed.y;
            }
            return context.dataset.label + ': ' + formatarMonetario(context.parsed.y, false);
          },
        },
      },
    },
    scales: {
      y: {
        type: 'linear',
        position: 'left',
        title: { display: true, text: 'VGV (R$)' },
        ticks: {
          callback: function(value) {
            return formatarMonetario(value, false);
          },
        },
      },
      y1: {
        type: 'linear',
        position: 'right',
        title: { display: true, text: 'Quantidade' },
        grid: { drawOnChartArea: false },
      },
    },
  }), []);

  const dadosGraficoQtdCorretores = useMemo(() => {
    if (!dados || !dados.quantidade_por_corretores || !dados.quantidade_por_corretores.length) return null;
    return {
      labels: dados.quantidade_por_corretores.map(item => item.categoria || 'N/I'),
      datasets: [
        {
          type: 'bar',
          label: 'Quantidade',
          data: dados.quantidade_por_corretores.map(item => item.quantidade || 0),
          backgroundColor: 'rgba(75, 192, 192, 0.7)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
          yAxisID: 'y',
          order: 2,
        },
        {
          type: 'line',
          label: 'Porcentagem (%)',
          data: dados.quantidade_por_corretores.map(item => item.porcentagem || 0),
          borderColor: 'rgba(255, 159, 64, 1)',
          backgroundColor: 'rgba(255, 159, 64, 0.2)',
          borderWidth: 2,
          pointRadius: 4,
          yAxisID: 'y1',
          order: 1,
        },
      ],
    };
  }, [dados]);

  const optionsGraficoQtdCorretores = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: function(context) {
            if (context.datasetIndex === 1) {
              return context.dataset.label + ': ' + context.parsed.y.toFixed(1) + '%';
            }
            return context.dataset.label + ': ' + context.parsed.y;
          },
        },
      },
    },
    scales: {
      y: {
        type: 'linear',
        position: 'left',
        title: { display: true, text: 'Quantidade' },
      },
      y1: {
        type: 'linear',
        position: 'right',
        title: { display: true, text: 'Porcentagem (%)' },
        grid: { drawOnChartArea: false },
        ticks: {
          callback: function(value) {
            return value + '%';
          },
        },
      },
    },
  }), []);

  const pizzaVgvImobiliaria = useMemo(
    () => montarDadosPizza(dados && dados.vgv_por_imobiliaria, 'VGV'),
    [dados]
  );

  const pizzaQtdImobiliaria = useMemo(
    () => montarDadosPizzaQuantidade(dados && dados.quantidade_por_imobiliaria, 'Quantidade'),
    [dados]
  );

  const optionsPizzaVgvImobiliaria = useMemo(
    () => criarOptionsPizza(pizzaVgvImobiliaria, true),
    [pizzaVgvImobiliaria]
  );

  const optionsPizzaQtdImobiliaria = useMemo(
    () => criarOptionsPizza(pizzaQtdImobiliaria, false),
    [pizzaQtdImobiliaria]
  );

  const buscarDados = useCallback(async filtro => {
    setLoading(true);
    try {
      const filtros = {
        data_inicial: filtro.data_inicial
          ? moment(filtro.data_inicial, 'DD/MM/YYYY').format('YYYY-MM-DD')
          : null,
        data_final: filtro.data_final
          ? moment(filtro.data_final, 'DD/MM/YYYY').format('YYYY-MM-DD')
          : null,
        codigo_empreendimento: filtro.codigo_empreendimento || null,
        codigo_imobiliaria: filtro.codigo_imobiliaria || null,
        somente_efetivadas: filtro.somente_efetivadas,
        incluir_vendas_sem_imobiliaria: filtro.somente_efetivadas
          ? filtro.incluir_vendas_sem_imobiliaria
          : false,
      };

      const response = await consultarDashboardVendasImobiliaria(filtros);
      setDados(response);
    } catch (err) {
      toast.error(err.message || 'Erro ao buscar dados');
      setDados(null);
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <div className="container-fluid py-3">
      <HeaderAzul title="Vendas por Imobiliária" />

      <Formik
        initialValues={{
          data_inicial: moment().subtract(30, 'days').format('DD/MM/YYYY'),
          data_final: moment().format('DD/MM/YYYY'),
          codigo_empreendimento: '',
          codigo_imobiliaria: '',
          somente_efetivadas: true,
          incluir_vendas_sem_imobiliaria: true,
        }}
        validationSchema={validationSchema}
        onSubmit={filtro => buscarDados(filtro)}
      >
        {({
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          resetForm,
          errors,
          touched,
          values,
        }) => (
          <>
            <Form
              onSubmit={handleSubmit}
              className="bg-white p-3 mb-3 rounded shadow-sm"
            >
              <Row>
                <Col xs={12} md={3}>
                  <Form.Group>
                    <Form.Label>Empreendimento</Form.Label>
                    <Select
                      key={`empreendimento-${formKey}`}
                      placeholder="Todos..."
                      options={optionsEmpreendimentos}
                      onChange={selected =>
                        setFieldValue('codigo_empreendimento', selected ? selected.value : '')
                      }
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      noOptionsMessage={() => 'Nenhuma opção'}
                      isClearable
                    />
                  </Form.Group>
                </Col>

                {(!dados || dados.exibir_secao_corretores !== false) && (
                  <Col xs={12} md={3}>
                    <Form.Group>
                      <Form.Label>Imobiliária</Form.Label>
                      <Select
                        key={`imobiliaria-${formKey}`}
                        placeholder="Todas..."
                        options={optionsImobiliarias}
                        onChange={selected =>
                          setFieldValue('codigo_imobiliaria', selected ? selected.value : '')
                        }
                        styles={customStyles}
                        components={{ DropdownIndicator }}
                        noOptionsMessage={() => 'Nenhuma opção'}
                        isClearable
                      />
                    </Form.Group>
                  </Col>
                )}

                <Col xs={12} md={2}>
                  <Form.Group>
                    <Form.Label>Data Inicial</Form.Label>
                    <div className="position-relative">
                      <MaskedInput
                        name="data_inicial"
                        type="tel"
                        guide={false}
                        className={`form-control ${
                          errors.data_inicial && touched.data_inicial ? 'is-invalid' : ''
                        }`}
                        placeholder="DD/MM/AAAA"
                        mask={constants.MASK_DATE}
                        value={values.data_inicial}
                        onChange={e => setFieldValue('data_inicial', e.target.value)}
                        onBlur={() => setFieldTouched('data_inicial', true)}
                        style={{ paddingRight: '65px' }}
                      />
                      <div
                        className="position-absolute d-flex align-items-center"
                        style={{ right: '10px', top: '50%', transform: 'translateY(-50%)', gap: '5px' }}
                      >
                        {errors.data_inicial && touched.data_inicial && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <BsTooltip id="tooltip-data-inicial">{errors.data_inicial}</BsTooltip>
                            }
                          >
                            <FaExclamationCircle className="text-danger" style={{ cursor: 'pointer' }} />
                          </OverlayTrigger>
                        )}
                        <FaCalendarAlt
                          className="text-secondary"
                          style={{ cursor: 'pointer' }}
                          onClick={() => setOpenDatePickerInicial(true)}
                        />
                      </div>
                      <MuiPickersUtilsProvider utils={MomentUtils} locale="pt-br">
                        <DatePicker
                          open={openDatePickerInicial}
                          onOpen={() => setOpenDatePickerInicial(true)}
                          onClose={() => setOpenDatePickerInicial(false)}
                          value={values.data_inicial ? moment(values.data_inicial, 'DD/MM/YYYY') : null}
                          onChange={date => {
                            setFieldValue('data_inicial', date ? date.format('DD/MM/YYYY') : '');
                            setOpenDatePickerInicial(false);
                          }}
                          format="DD/MM/YYYY"
                          TextFieldComponent={() => null}
                        />
                      </MuiPickersUtilsProvider>
                    </div>
                  </Form.Group>
                </Col>

                <Col xs={12} md={2}>
                  <Form.Group>
                    <Form.Label>Data Final</Form.Label>
                    <div className="position-relative">
                      <MaskedInput
                        name="data_final"
                        type="tel"
                        guide={false}
                        className={`form-control ${
                          errors.data_final && touched.data_final ? 'is-invalid' : ''
                        }`}
                        placeholder="DD/MM/AAAA"
                        mask={constants.MASK_DATE}
                        value={values.data_final}
                        onChange={e => setFieldValue('data_final', e.target.value)}
                        onBlur={() => setFieldTouched('data_final', true)}
                        style={{ paddingRight: '65px' }}
                      />
                      <div
                        className="position-absolute d-flex align-items-center"
                        style={{ right: '10px', top: '50%', transform: 'translateY(-50%)', gap: '5px' }}
                      >
                        {errors.data_final && touched.data_final && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <BsTooltip id="tooltip-data-final">{errors.data_final}</BsTooltip>
                            }
                          >
                            <FaExclamationCircle className="text-danger" style={{ cursor: 'pointer' }} />
                          </OverlayTrigger>
                        )}
                        <FaCalendarAlt
                          className="text-secondary"
                          style={{ cursor: 'pointer' }}
                          onClick={() => setOpenDatePickerFinal(true)}
                        />
                      </div>
                      <MuiPickersUtilsProvider utils={MomentUtils} locale="pt-br">
                        <DatePicker
                          open={openDatePickerFinal}
                          onOpen={() => setOpenDatePickerFinal(true)}
                          onClose={() => setOpenDatePickerFinal(false)}
                          value={values.data_final ? moment(values.data_final, 'DD/MM/YYYY') : null}
                          onChange={date => {
                            setFieldValue('data_final', date ? date.format('DD/MM/YYYY') : '');
                            setOpenDatePickerFinal(false);
                          }}
                          format="DD/MM/YYYY"
                          TextFieldComponent={() => null}
                        />
                      </MuiPickersUtilsProvider>
                    </div>
                  </Form.Group>
                </Col>

                <Col xs={12} md={2} className="d-flex align-items-end pb-3">
                  <div>
                    <Form.Check
                      type="checkbox"
                      id="somente_efetivadas"
                      label="Somente Efetivadas"
                      checked={values.somente_efetivadas}
                      onChange={e => {
                        setFieldValue('somente_efetivadas', e.target.checked);
                        if (!e.target.checked) {
                          setFieldValue('incluir_vendas_sem_imobiliaria', false);
                        }
                      }}
                    />
                    <Form.Check
                      type="checkbox"
                      id="incluir_vendas_sem_imobiliaria"
                      label="Vendas s/ Imobiliária"
                      checked={values.incluir_vendas_sem_imobiliaria}
                      disabled={!values.somente_efetivadas}
                      onChange={e =>
                        setFieldValue('incluir_vendas_sem_imobiliaria', e.target.checked)
                      }
                    />
                  </div>
                </Col>
              </Row>

              <div className="d-flex justify-content-end align-items-center mt-2">
                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    resetForm();
                    setFormKey(prev => prev + 1);
                    setDados(null);
                  }}
                  className="mr-2"
                  type="button"
                >
                  <FaEraser className="mr-1" /> Limpar
                </Button>
                <Button
                  type="submit"
                  variant="success"
                  disabled={
                    loading ||
                    values.data_inicial === '' ||
                    values.data_final === '' ||
                    (errors.data_inicial && touched.data_inicial) ||
                    (errors.data_final && touched.data_final)
                  }
                >
                  {loading ? (
                    <Spinner animation="border" size="sm" className="mr-1" />
                  ) : (
                    <FaSearch className="mr-1" />
                  )}
                  Buscar
                </Button>
              </div>
            </Form>

            {dados ? (
              <>
                <Row className="mb-3">
                  <Col xs={12} md={4}>
                    <Card className="text-center shadow-sm">
                      <Card.Body>
                        <Card.Title className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>
                          VGV Total Imobiliárias
                        </Card.Title>
                        <h4 className="mb-0 text-primary">
                          {formatarMonetario(dados.vgv_total_imobiliarias, false)}
                        </h4>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col xs={12} md={4}>
                    <Card className="text-center shadow-sm">
                      <Card.Body>
                        <Card.Title className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>
                          Qtd. Imobiliárias
                        </Card.Title>
                        <h4 className="mb-0 text-primary">
                          {(dados.quantidade_total_imobiliarias || 0).toLocaleString('pt-BR')}
                        </h4>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col xs={12} md={4}>
                    <Card className="text-center shadow-sm">
                      <Card.Body>
                        <Card.Title className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>
                          Qtd. Vendas
                        </Card.Title>
                        <h4 className="mb-0 text-primary">
                          {(dados.quantidade_vendas || 0).toLocaleString('pt-BR')}
                        </h4>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col xs={12} md={6}>
                    <Card className="text-center shadow-sm">
                      <Card.Body>
                        <Card.Title className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>
                          VGV Total Corretores
                        </Card.Title>
                        <h4 className="mb-0 text-success">
                          {formatarMonetario(dados.vgv_total_corretores, false)}
                        </h4>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card className="text-center shadow-sm">
                      <Card.Body>
                        <Card.Title className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>
                          Qtd. Corretores
                        </Card.Title>
                        <h4 className="mb-0 text-success">
                          {(dados.quantidade_corretores || 0).toLocaleString('pt-BR')}
                        </h4>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col xs={12} md={6}>
                    <Card className="h-100 shadow-sm">
                      <Card.Header className="bg-primary text-white">
                        <FaChartPie className="mr-2" />
                        VGV por Imobiliária
                      </Card.Header>
                      <Card.Body style={{ height: '350px' }}>
                        {pizzaVgvImobiliaria ? (
                          <Pie
                            data={pizzaVgvImobiliaria}
                            options={optionsPizzaVgvImobiliaria}
                          />
                        ) : (
                          <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                            Sem dados para exibir
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col xs={12} md={6}>
                    <Card className="h-100 shadow-sm">
                      <Card.Header className="bg-primary text-white">
                        <FaChartPie className="mr-2" />
                        Quantidade por Imobiliária
                      </Card.Header>
                      <Card.Body style={{ height: '350px' }}>
                        {pizzaQtdImobiliaria ? (
                          <Pie
                            data={pizzaQtdImobiliaria}
                            options={optionsPizzaQtdImobiliaria}
                          />
                        ) : (
                          <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                            Sem dados para exibir
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col xs={12}>
                    <Card className="shadow-sm">
                      <Card.Header className="bg-success text-white">
                        VGV por Corretor
                      </Card.Header>
                      <Card.Body style={{ height: '400px' }}>
                        {dadosGraficoVgvCorretores ? (
                          <Chart
                            type="bar"
                            data={dadosGraficoVgvCorretores}
                            options={optionsGraficoVgvCorretores}
                          />
                        ) : (
                          <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                            Sem dados para exibir
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col xs={12}>
                    <Card className="shadow-sm">
                      <Card.Header className="bg-success text-white">
                        Quantidade por Corretor
                      </Card.Header>
                      <Card.Body style={{ height: '400px' }}>
                        {dadosGraficoQtdCorretores ? (
                          <Chart
                            type="bar"
                            data={dadosGraficoQtdCorretores}
                            options={optionsGraficoQtdCorretores}
                          />
                        ) : (
                          <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                            Sem dados para exibir
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </>
            ) : (
              !loading && (
                <div className="bg-white p-5 mt-3 text-center rounded shadow-sm">
                  <Row>
                    <Col>
                      <FaExclamationCircle size={48} className="text-muted mb-3" />
                      <h5 className="text-muted">Nenhum registro encontrado</h5>
                      <p className="text-muted">
                        Utilize os filtros acima para buscar dados.
                      </p>
                    </Col>
                  </Row>
                </div>
              )
            )}
          </>
        )}
      </Formik>
    </div>
  );
}
